import { ContractInfo } from 'api/types/httpsTypes/contracts';
import {
  commonOptions,
  TVChartContainer,
  useTvChart,
} from 'modules/tw-chart/TVChartContainer';
import { createDatafeed, solanaResolutionToTimeframe } from './datafeed';
import {
  ChartingLibraryWidgetOptions,
  IChartingLibraryWidget,
  Overrides,
  ResolutionString,
  ThemeName,
} from 'modules/tw-chart/charting_library';
import { useChartEffect } from 'modules/smart-trade-terminal/helpers/use-chart-effect';
import { chainAsset, Chains, Protocols } from 'api/types/httpsTypes/d-wallets';
import { useEffect, useMemo, useRef } from 'react';
import createPersistedState from 'use-persisted-state';
import { useWebsocket } from 'api/hooks/useWebsocket';
import { useTokenChartWebsocket } from 'api/hooks/useTokenChartWebsocket';
import { formatNumberWithSuffix } from 'utils/FormatNumber';

type Props = {
  contract: ContractInfo;
  poolAddress: string;
  chartHeight?: number;
};

const dexTvChartSettings = 'dex-tv-chart-settings';

interface ChartSettingsProps {
  interval: ResolutionString;
  currencyIsUsd: boolean;
  isPriceShown: boolean;
}

export const useDexTvChartSettings =
  createPersistedState<ChartSettingsProps>(dexTvChartSettings);

export function DexChart({ contract, poolAddress }: Props) {
  const [chart, onChange] = useTvChart();
  const [chartSettings, setChartSettings] = useDexTvChartSettings({
    interval: '15' as ResolutionString,
    currencyIsUsd: true,
    isPriceShown: true,
  });

  const chartSettingsRef = useRef(chartSettings);

  useEffect(() => {
    chartSettingsRef.current = chartSettings;
  }, [chartSettings]);

  const chartInstanceRef = useRef<any>(null);

  const tvSymbol = useMemo(() => {
    const base = `${contract.generalInfo.symbol || 'Token'}:${
      contract.generalInfo.address
    }:${poolAddress}`;
    return `${base}?usd=${chartSettings.currencyIsUsd}&isPrice=${chartSettings.isPriceShown}`;
  }, [
    contract,
    poolAddress,
    chartSettings.currencyIsUsd,
    chartSettings.isPriceShown,
  ]);

  useEffect(() => {
    if (!chart) return;

    chartInstanceRef.current = chart;

    const interval = setInterval(() => {
      if (chart?.chart?.()) {
        chart.chart().setResolution(chartSettingsRef.current.interval);
        clearInterval(interval);

        chart
          .chart()
          .onIntervalChanged()
          .subscribe(null, (newInterval) => {
            setChartSettings((prev) => ({
              ...prev,
              interval: newInterval as ResolutionString,
            }));
          });
      }
    }, 100);

    return () => clearInterval(interval);
  }, [chart]);

  useSwitch(
    chart,
    () =>
      setChartSettings({
        ...chartSettingsRef.current,
        currencyIsUsd: !chartSettings.currencyIsUsd,
      }),
    chartSettings.currencyIsUsd,
    'USD',
    chainAsset(contract.generalInfo.chain.key)
  );

  useSwitch(
    chart,
    () => {
      setChartSettings({
        ...chartSettingsRef.current,
        isPriceShown: !chartSettings.isPriceShown,
      });
    },
    chartSettings.isPriceShown,
    'Price',
    'MarketCap'
  );

  const chartSubscription = useMemo(
    () => ({
      chain: contract.generalInfo.chain.key,
      poolAddress,
      interval: solanaResolutionToTimeframe(chartSettings.interval),
      currency: chartSettings.currencyIsUsd ? 'usd' : 'quote',
    }),
    [
      contract.generalInfo.chain.key,
      poolAddress,
      chartSettings.interval,
      chartSettings.currencyIsUsd,
    ]
  );

  useWebsocket('token_chart', chartSubscription);

  useTokenChartWebsocket(
    contract.generalInfo.chain.key,
    poolAddress,
    solanaResolutionToTimeframe(chartSettings.interval),
    chartSettings.currencyIsUsd ? 'usd' : 'quote',
    chartSettings.isPriceShown,
    contract.generalInfo.totalSupply ?? 0
  );

  return (
    <div className="dex-chart-wrapper">
      <TVChartContainer
        optionsFactory={(theme, defaults) =>
          createOptions(
            theme,
            defaults,
            contract.liquidityInfo.activePools[0].priceDollar || undefined,
            contract.generalInfo.chain.key,
            contract.liquidityInfo.activePools[0].dex.key,
            chartSettings.currencyIsUsd,
            chartSettings.isPriceShown,
            contract.generalInfo.totalSupply ?? 0
          )
        }
        onWidgetChange={onChange}
        symbol={tvSymbol}
      />
    </div>
  );
}

export function useSwitch(
  chart: IChartingLibraryWidget | null,
  toggle: () => void,
  isFunction: boolean,
  first: string,
  second: string
) {
  const buttonRef = useRef<HTMLElement | null>(null);

  // Create button once
  useChartEffect(
    chart,
    (widget) => {
      let isMounted = true;

      widget
        .headerReady()
        .then(() => {
          if (!isMounted) return;

          const button = widget.createButton({
            useTradingViewStyle: false,
            align: 'left',
          });

          buttonRef.current = button;

          button.onclick = toggle;

          button.style.cursor = 'pointer';

          updateButtonContent(button, isFunction, first, second);
        })
        .catch((err) => {
          console.error('Failed to wait for TradingView header:', err);
        });

      return () => {
        isMounted = false;
        if (buttonRef.current) {
          buttonRef.current.remove();
          buttonRef.current = null;
        }
      };
    },
    [chart]
  );

  // Update content when state changes
  useEffect(() => {
    if (buttonRef.current) {
      updateButtonContent(buttonRef.current, isFunction, first, second);
    }
  }, [isFunction]);
}

function updateButtonContent(
  button: HTMLElement,
  firstActive: boolean,
  first: string,
  second: string
) {
  button.innerHTML = `
    <div style="display: flex; gap: 4px; align-items: center;">
      <span style="color: ${
        firstActive ? '#3392e8' : '#818ea3'
      };">${first}</span>
      <span style="color: #818ea3;">/</span>
      <span style="color: ${
        firstActive ? '#818ea3' : '#3392e8'
      };">${second}</span>
    </div>
  `;
}

export const createOptions = (
  theme: ThemeName,
  defaults: Partial<ChartingLibraryWidgetOptions>,
  referencePrice: number | undefined,
  chain: Chains,
  dex: Protocols | undefined,
  currencyIsUsd?: boolean,
  isPriceShown?: boolean,
  totalTokensSupply?: number
) => {
  return {
    ...commonOptions,
    interval: '15' as ResolutionString,
    datafeed: createDatafeed(
      chain,
      dex,
      referencePrice,
      currencyIsUsd,
      isPriceShown,
      totalTokensSupply
    ),
    custom_css_url: '/chart-overrides-dex.css',
    enabled_features: [
      'snapshot_trading_drawings',
      'timeframes_toolbar',
      'seconds_resolution',
    ],
    custom_formatters: {
      priceFormatterFactory: (symbolInfo) => {
        if (symbolInfo === null) {
          return null;
        }

        if (symbolInfo.format === 'volume') {
          return {
            format: (price) => formatNumberWithSuffix(price, { precision: 4 }),
          };
        }
        if (symbolInfo.format === 'price') {
          return {
            format: (price) => formatNumberWithSuffix(price, { precision: 4 }),
          };
        }
      },
    },
    toolbar_bg: '#121212FF',
    overrides: createOverrides(),
    studies_overrides: {
      'volume.volume.color.0': 'rgba(240, 72, 102, 0.29)',
      'volume.volume.color.1': 'rgba(136, 214, 147, 0.29)',
      'volume.volume.transparency': 85,
    },
    loading_screen: {
      backgroundColor: '#121212FF',
      foregroundColor: '#3392E8FF',
    },

    custom_font_family: "'Open Sans'",
    time_frames: [
      { text: '1D', resolution: '15', description: '1 Day', title: '1D' },
      { text: '5D', resolution: '60', description: '5 Days', title: '5D' },
      { text: '1M', resolution: '60', description: '1 Month', title: '1M' },
      { text: '3M', resolution: '1D', description: '3 Months', title: '3M' },
      { text: '6M', resolution: '1D', description: '6 Months', title: '6M' },
    ],
    ...defaults,
    theme,
  } as ChartingLibraryWidgetOptions;
};

function createOverrides() {
  return {
    'paneProperties.backgroundType': 'solid',
    'paneProperties.background': 'rgba(18, 18, 18, 1)', // Opaque background
    'paneProperties.vertGridProperties.color': 'rgba(34, 36, 39, 1)',
    'paneProperties.horzGridProperties.color': 'rgba(34, 36, 39, 1)',
    'paneProperties.crossHairProperties.color': 'rgba(34, 36, 39, 1)',

    'paneProperties.legendProperties.backgroundTransparency': 0,
    'scalesProperties.axisLineToolLabelBackgroundColorActive':
      'rgba(51, 146, 232, 1)',
    'scalesProperties.axisLineToolLabelBackgroundColorCommon':
      'rgba(51, 146, 232, 1)',
    'scalesProperties.lineColor': 'rgba(18, 18, 18, 1)',
    'scalesProperties.textColor': 'rgba(245, 245, 245, 1)',
    'scalesProperties.fontSize': 12,
    'mainSeriesProperties.candleStyle.upColor': 'rgba(136, 214, 147, 0.68)',
    'mainSeriesProperties.candleStyle.drawBorder': false,
    'mainSeriesProperties.candleStyle.wickUpColor': 'rgba(136, 214, 147, 0.68)',
    'mainSeriesProperties.candleStyle.downColor': 'rgba(240, 72, 102, 0.68)',
    'mainSeriesProperties.candleStyle.wickDownColor':
      'rgba(240, 72, 102, 0.68)',
  } as Overrides;
}
